import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { INR } from "@/lib/format";
import StatementView from "@/components/StatementView";
import CustomerTasksPanel from "@/components/CustomerTasksPanel";
import CustomerListPanel from "@/components/CustomerListPanel";
import DayBook from "@/components/DayBook";
import PreparerReport from "@/components/PreparerReport";
import RealizationReport from "@/components/RealizationReport";
import DateRangeBar, { rangeParams } from "@/components/DateRangeBar";
import { useAuth } from "@/context/AuthContext";
import { NoAccess } from "@/components/Can";
import { PrintButton, PrintHeader } from "@/components/PrintButton";
import ExcelButton from "@/components/ExcelButton";
import { exportExcel, n, stamp } from "@/lib/excel";

export default function Reports() {
  const { can } = useAuth();
  const hasReports = can("reports");
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || (hasReports ? "balance" : "statement");
  const stmtCustomer = sp.get("customer") || "";
  const setTab = (id) => setSp(prev => { const n = new URLSearchParams(prev); n.set("tab", id); return n; });
  const setStmtCustomer = (cid) => setSp(prev => { const n = new URLSearchParams(prev); cid ? n.set("customer", cid) : n.delete("customer"); return n; });
  const openStatement = (cid) => setSp(prev => { const n = new URLSearchParams(prev); n.set("tab", "statement"); cid ? n.set("customer", cid) : n.delete("customer"); return n; });
  const [balance, setBalance] = useState([]);
  const [prodSales, setProdSales] = useState([]);
  const [prodByCust, setProdByCust] = useState([]);
  const [salesRange, setSalesRange] = useState({ from: "", to: "" });
  const [company, setCompany] = useState(null);
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const has = (...vals) => !ql || vals.some(v => String(v ?? "").toLowerCase().includes(ql));
  const fBalance = balance.filter(c => has(c.name, c.phone, c.balance));
  const fProd = prodSales.filter(p => has(p.product_name, p.unit));
  const fPxc = prodByCust.filter(p => has(p.customer_name, p.product_name));
  const period = `${salesRange.from || "Start"} → ${salesRange.to || "Today"}`;
  const excelBalance = () => exportExcel(`CustomerBalances-${stamp()}`, [{ name: "Balances", title: "Customer Balances", subtitle: q ? `Filter: ${q}` : "All customers",
    columns: [{ header: "Customer", get: c => c.name, width: 30 }, { header: "Phone", get: c => c.phone || "" }, { header: "Balance", get: c => n(c.balance), num: true }], rows: fBalance, footer: ["Total Outstanding", "", fBalance.reduce((s,x)=>s+x.balance,0)] }], company);
  const excelProd = () => exportExcel(`ProductSales-${salesRange.from || "start"}-${salesRange.to || "today"}`, [{ name: "Product Sales", title: "Product Sales", subtitle: period,
    columns: [{ header: "Product", get: p => p.product_name, width: 30 }, { header: "Unit", get: p => p.unit || "" }, { header: "Qty", get: p => n(p.qty), num: true }, { header: "Revenue", get: p => n(p.revenue), num: true }], rows: fProd, footer: ["Total Revenue", "", fProd.reduce((s,x)=>s+x.qty,0), fProd.reduce((s,x)=>s+x.revenue,0)] }], company);
  const excelPxc = () => exportExcel(`SalesByCustomer-${salesRange.from || "start"}-${salesRange.to || "today"}`, [{ name: "Sales by Customer", title: "Sales by Customer", subtitle: period,
    columns: [{ header: "Customer", get: p => p.customer_name, width: 30 }, { header: "Product", get: p => p.product_name, width: 30 }, { header: "Qty", get: p => n(p.qty), num: true }, { header: "Avg Rate", get: p => n(p.avg_rate), num: true }, { header: "Revenue", get: p => n(p.revenue), num: true }], rows: fPxc, footer: ["Total Revenue", "", "", "", fPxc.reduce((s,x)=>s+x.revenue,0)] }], company);

  useEffect(()=>{
    Promise.all([api.get("/reports/customer-balance"), api.get("/company")])
      .then(([a,d])=>{ setBalance(a.data); setCompany(d.data); });
  }, []);

  useEffect(()=>{
    if (!hasReports) return;
    const params = rangeParams(salesRange);
    Promise.all([api.get("/reports/product-sales", { params }), api.get("/reports/product-sales-by-customer", { params })])
      .then(([b,c])=>{ setProdSales(b.data); setProdByCust(c.data); });
  }, [salesRange, hasReports]);

  const TabBtn = ({ id, label }) => (
    <button data-testid={`tab-${id}`} onClick={()=>setTab(id)} className={`px-3.5 py-2 text-xs font-semibold uppercase tracking-wider rounded-md ${tab===id ? "bg-slate-900 text-white" : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{label}</button>
  );

  return (
    <div className="space-y-4" data-testid="reports-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{fontFamily:'Plus Jakarta Sans'}}>Reports</h1>
          <p className="text-sm text-slate-500">Balances, product sales, and registers.</p></div>
      </div>

      {!hasReports && <NoAccess what="Day Book and sales reports (customer statements remain available)" />}

      <div className="flex flex-wrap gap-2 items-center">
        <TabBtn id="balance" label="Customer Balances"/>
        <TabBtn id="statement" label="Customer Statement"/>
        {hasReports && <><TabBtn id="daybook" label="Day Book"/>
        <TabBtn id="prod" label="Product Sales"/>
        <TabBtn id="pxc" label="Sales by Customer"/>
        <TabBtn id="preparer" label="By Preparer"/>
        <TabBtn id="realization" label="Realization"/></>}
        {tab !== "statement" && <input data-testid="reports-search" placeholder="Search customer, product…" value={q} onChange={(e)=>setQ(e.target.value)} className="no-print sm:ml-auto h-9 px-3 border border-slate-300 rounded-md text-sm bg-white outline-none focus:border-blue-600 w-full sm:w-56"/>}
      </div>

      {tab==="daybook" && <DayBook company={company} search={q} />}
      {tab==="preparer" && <PreparerReport company={company} search={q} />}
      {tab==="realization" && <RealizationReport company={company} search={q} onOpenCustomer={openStatement} />}

      {tab==="statement" && (
        <div className="grid lg:grid-cols-[300px_1fr] gap-4 items-start">
          <CustomerListPanel testid="stmt-customer" customers={balance} value={stmtCustomer} onChange={setStmtCustomer} />
          <div className="space-y-4 min-w-0">
            <StatementView customerId={stmtCustomer} company={company} />
            {stmtCustomer && <CustomerTasksPanel customerId={stmtCustomer} />}
          </div>
        </div>
      )}

      {tab==="balance" && (
        <div className="space-y-2">
          <div className="flex justify-end gap-2"><ExcelButton testid="balance-excel-btn" onClick={excelBalance}/><PrintButton testid="balance-print-btn"/></div>
          <PrintHeader company={company} title="Customer Balances" subtitle={q ? `Filter: ${q}` : "All customers"}/>
          <div className="bg-white border border-slate-200 rounded-md table-scroll">
          <table className="acct-table fit"><thead><tr><th>Customer</th><th>Phone</th><th className="num">Balance</th></tr></thead>
          <tbody>{fBalance.map(c=>(
            <tr key={c.id}><td className="font-medium"><button data-testid={`balance-open-${c.id}`} onClick={()=>openStatement(c.id)} className="hover:underline text-blue-700">{c.name}</button></td><td className="mono text-slate-500">{c.phone}</td>
            <td className={`num mono font-medium ${c.balance>0?"text-rose-800":c.balance<0?"text-emerald-800":""}`}>{INR(c.balance)}</td></tr>
          ))}
          {!fBalance.length && <tr><td colSpan="3" className="text-center py-8 text-slate-400">No customers</td></tr>}
          <tr className="bg-slate-100 font-bold"><td colSpan="2">Total Outstanding</td><td className="num mono">{INR(fBalance.reduce((s,x)=>s+x.balance,0))}</td></tr>
          </tbody></table>
        </div>
        </div>
      )}

      {(tab==="prod" || tab==="pxc") && <DateRangeBar range={salesRange} onChange={setSalesRange} testid="sales"><ExcelButton testid="sales-excel-btn" onClick={tab==="prod" ? excelProd : excelPxc}/><PrintButton testid="sales-print-btn"/></DateRangeBar>}

      {tab==="prod" && (
        <div className="space-y-2">
          <PrintHeader company={company} title="Product Sales" subtitle={`${salesRange.from || "Start"} → ${salesRange.to || "Today"}`}/>
          <div className="bg-white border border-slate-200 rounded-md table-scroll">
          <table className="acct-table fit" data-testid="product-sales-table"><thead><tr><th>Product</th><th>Unit</th><th className="num">Qty</th><th className="num">Revenue</th></tr></thead>
          <tbody>{fProd.map(p=>(
            <tr key={p.product_id}><td className="font-medium">{p.product_name}</td><td className="mono text-slate-500">{p.unit}</td>
            <td className="num mono">{Number(p.qty).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
            <td className="num mono font-medium">{INR(p.revenue)}</td></tr>
          ))}
          {!fProd.length && <tr><td colSpan="4" className="text-center py-8 text-slate-400">No sales in this period</td></tr>}
          <tr className="bg-slate-100 font-bold"><td colSpan="3">Total Revenue</td><td className="num mono" data-testid="product-sales-total">{INR(fProd.reduce((s,x)=>s+x.revenue,0))}</td></tr>
          </tbody></table>
        </div>
        </div>
      )}

      {tab==="pxc" && (
        <div className="space-y-2">
          <PrintHeader company={company} title="Sales by Customer" subtitle={`${salesRange.from || "Start"} → ${salesRange.to || "Today"}`}/>
          <div className="bg-white border border-slate-200 rounded-md table-scroll">
          <table className="acct-table" data-testid="sales-by-customer-table"><thead><tr><th>Customer</th><th>Product</th><th className="num">Qty</th><th className="num">Avg Rate</th><th className="num">Revenue</th></tr></thead>
          <tbody>{fPxc.map((p,i)=>(
            <tr key={i}><td>{p.customer_name}</td><td className="font-medium">{p.product_name}</td>
            <td className="num mono">{Number(p.qty).toLocaleString("en-IN",{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
            <td className="num mono">{INR(p.avg_rate)}</td><td className="num mono font-medium">{INR(p.revenue)}</td></tr>
          ))}
          {!fPxc.length && <tr><td colSpan="5" className="text-center py-8 text-slate-400">No sales in this period</td></tr>}
          <tr className="bg-slate-100 font-bold"><td colSpan="4">Total Revenue</td><td className="num mono" data-testid="sales-by-customer-total">{INR(fPxc.reduce((s,x)=>s+x.revenue,0))}</td></tr>
          </tbody></table>
        </div>
        </div>
      )}
    </div>
  );
}
